import ReactMarkdown from 'react-markdown';
import { FileText, Loader2, X } from 'lucide-react';
import { FullScreenTakeover } from '../../components/FullScreenTakeover';
import { Button } from '../../components/ui/button';
import {
  markdownComponents,
  remarkPlugins,
} from '../../components/markdown/artifactMarkdown';
import { useRequestArtifacts } from '../../hooks/useSupportRequests';
import { ThreadPanel } from './ThreadPanel';

/**
 * Read an artifact full-screen without leaving the conversation.
 *
 * Document on the left, the conversation it was posted in on the right — the
 * point being that someone can read a long report and weigh in as they go,
 * rather than reading it, navigating back, and trying to remember what they
 * wanted to say.
 *
 * The right-hand side is the message thread, not the artifact's own comment
 * thread (which is what the standalone artifact page shows). Comments on an
 * artifact are a different conversation from the request that produced it, and
 * mixing them would split the discussion across two places.
 *
 * Both cases from the spec are handled by ThreadPanel resolving the root
 * server-side: if the artifact was posted top-level, the thread is that message
 * plus its replies; if it was posted inside a thread, the thread is the parent
 * plus all of its replies, with the artifact's own message among them.
 */
export function ArtifactTakeover({
  requestId,
  artifactId,
  messageId,
  onClose,
  onOpenArtifact,
}: {
  requestId: string;
  artifactId: string | null;
  messageId: string | null;
  onClose: () => void;
  /** Lets a *different* artifact in the same thread swap into the takeover. */
  onOpenArtifact?: (artifactId: string, messageId: string) => void;
}) {
  const { data: artifacts } = useRequestArtifacts(requestId);
  const artifact = artifacts?.find((a) => a.id === artifactId);
  const open = !!artifactId && !!messageId;

  return (
    <FullScreenTakeover show={open} onHide={onClose} noCloseButton>
      <div className="flex h-full min-h-0">
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="flex items-start gap-2 px-6 py-4 border-b border-border flex-shrink-0 min-w-0">
            <FileText className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-foreground truncate">
                {artifact?.name ?? 'Artifact'}
              </h1>
              {artifact?.description && (
                <p className="text-sm text-muted-foreground truncate">
                  {artifact.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {!artifact ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading artifact
              </div>
            ) : (
              <div className="px-12 py-10">
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown
                    remarkPlugins={remarkPlugins}
                    components={markdownComponents}
                  >
                    {artifact.markdown}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </div>

        <aside className="w-96 flex-shrink-0 border-l border-border bg-muted/20 min-h-0">
          {open && (
            <ThreadPanel
              requestId={requestId}
              messageId={messageId as string}
              emptyHint="No replies yet. Comment as you read."
              // The artifact on screen drops its "View full" — it would only
              // reopen what the reader already has open.
              openArtifactId={artifactId}
              onOpenArtifact={onOpenArtifact}
              headerAction={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="h-7 w-7 flex-shrink-0"
                  title="Close"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              }
              contextCard={
                artifact ? (
                  <div className="rounded-md border border-border bg-card px-3 py-2">
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <FileText className="h-3 w-3" />
                      You're reading
                    </div>
                    <p className="text-sm font-medium text-foreground truncate mt-0.5">
                      {artifact.name}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Shared in this thread
                    </p>
                  </div>
                ) : null
              }
            />
          )}
        </aside>
      </div>
    </FullScreenTakeover>
  );
}
